import { gql, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import type { ICollectionHandleQuery } from "@repo/shopify/src/queries/collection-gql";
import { collectionHandleQuery } from "@repo/shopify/src/queries/collection-gql";
import type { ProductEdge } from "@shopify/hydrogen-react/storefront-api-types";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { CATEGORY, COLLECTION_SORT } from "@const/constants";

const useCollections = (loaderEl?: MutableRefObject<Element | null>) => {
  const navigate = useNavigate();
  const { handle } = useParams();
  // archive sale handle만 처리하기 위한 코드
  const archiveSaleHandle = useMemo(() => {
    if (!handle) return "all-archive-sale";
    if (handle?.includes("archive")) return handle;
    return "";
  }, [handle]);

  const [searchParams] = useSearchParams();

  const filter = searchParams.get("filter");
  const sortIndex = Number(searchParams.get("sortIndex")) || 0;
  const selectedTag = searchParams.get("type");

  const [data, setData] = useState<ICollectionHandleQuery>(); // 컬렉션 쿼리 데이터
  const [products, setProducts] = useState<ProductEdge[]>([]); // 컬렉션 상품 목록

  const isTriggeredRef = useRef(false);
  const prevFilter = useRef<Record<string, string | Record<string, string>>[]>([]);

  /** 컬렉션 데이터 다음페이지 있는지 여부 */
  const hasNextPage = useMemo(
    () => data?.collection.products.pageInfo.hasNextPage || false,
    [data?.collection.products.pageInfo],
  );

  /** 컬렉션 데이터 마지막 cursor */
  const endCursor = useMemo(() => data?.collection.products.pageInfo.endCursor, [data?.collection.products.pageInfo]);

  /** 쿼리 조회를 하기 위한 filter, handle, sort 정보 variable */
  const variables = useMemo(() => {
    const filterQueries: Record<string, string | Record<string, string>>[] = [];
    if (filter) {
      // 브라우저가 자동으로 디코딩 하는걸 방지하기 위해 이중으로 해줌.(다시 JSON으로 복원하려함)
      const decodeFilter = decodeURIComponent(decodeURIComponent(filter));
      // JSON.parse 의 반환 타입은 기본적으로 any 이므로 타입 단언을 해주었음.
      const filterObj = JSON.parse(decodeFilter) as Record<string, string[]>;

      Object.entries(filterObj).forEach(([key, value]) => {
        if (key === "brand" && value.length) filterQueries.push(...value.map((item) => ({ productVendor: item })));
        if (key === "category" && value.length) filterQueries.push(...value.map((item) => ({ productType: item })));
        if (key === "size" && value.length)
          filterQueries.push(...value.map((item) => ({ variantOption: { name: "size", value: item } })));
        if (key === "color" && value.length)
          filterQueries.push(
            ...value.map((item) => ({ productMetafield: { key: "color", namespace: "filter", value: item } })),
          );
        if (["Clothing", "Accessories", "Footwear"].includes(key) && value.length)
          filterQueries.push(
            ...value.map((item) => ({ productMetafield: { key: "sub", namespace: CATEGORY, value: item } })),
          );
        if (key === "sale" && value.length)
          filterQueries.push({ productMetafield: { key: "sale", namespace: "operation", value: "true" } });
      });
    }

    return {
      handle: archiveSaleHandle,
      first: 24,
      sortKey: COLLECTION_SORT[sortIndex]?.value ?? "COLLECTION_DEFAULT",
      reverse: COLLECTION_SORT[sortIndex]?.reverse ?? false,
      filters: filterQueries,
    };
  }, [archiveSaleHandle, filter, sortIndex]);

  /** 컬렉션 데이터 쿼리 */
  const { loading, fetchMore, networkStatus, refetch, client } = useQuery<ICollectionHandleQuery>(
    gql(collectionHandleQuery.query),
    {
      variables,
      notifyOnNetworkStatusChange: true,
      onCompleted: (_data) => {
        if (!_data.collection) {
          navigate("/404");
          return;
        }

        prevFilter.current = variables.filters;

        setData(_data);
        setProducts(_data.collection.products.edges.filter(({ node }) => Boolean(node.publishedAt)));
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][Collection] Failed to query", error);
        throw new Error(`[SHOPIFY][Collection] Failed to query : ${JSON.stringify(error)}`);
      },
    },
  );

  useEffect(() => {
    prevFilter.current = variables.filters;
  }, []);

  // useEffect(() => {
  //   const tuneHandle = sessionStorage.getItem("tune_handle");
  //   if (tuneHandle !== handle) {
  //     refetchData();
  //   }
  //
  //   sessionStorage.setItem("tune_handle", handle || "");
  // }, [archiveSaleHandle]);

  useEffect(() => {
    const tuneFilter = sessionStorage.getItem("tune_filter");
    if (
      JSON.stringify(prevFilter.current) !== JSON.stringify(variables.filters) ||
      tuneFilter !== JSON.stringify(variables.filters)
    ) {
      refetchData();
    }

    sessionStorage.setItem("tune_filter", JSON.stringify(variables.filters));
  }, [variables.filters]);

  const refetchData = async () => {
    const { data: fetchedData } = await refetch();
    if (fetchedData) {
      setData(fetchedData);
      setProducts(
        fetchedData.collection
          ? fetchedData.collection.products.edges.filter(({ node }) => Boolean(node.publishedAt))
          : [],
      );
    }
  };

  /**
   * InterSectionObserver handler
   * 컬렉션 데이터 추가 쿼리
   * */
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (isTriggeredRef.current) return;

      const target = entries[0];
      if (target.isIntersecting && hasNextPage) {
        isTriggeredRef.current = true;
        fetchMore({
          variables: { ...variables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            const edges = fetchMoreResult.collection.products.edges;
            const pageInfo = fetchMoreResult.collection.products.pageInfo;
            return {
              collection: {
                ...prev.collection,
                products: {
                  ...prev.collection.products,
                  edges: [...prev.collection.products.edges, ...edges],
                  pageInfo,
                },
              },
            };
          },
        })
          .then(({ data: fetchedData }: { data: ICollectionHandleQuery }) => {
            setData(fetchedData);
            setProducts((prev) => {
              const nextProds = fetchedData.collection.products.edges.filter(({ node }) => Boolean(node.publishedAt));
              return [...prev, ...nextProds];
            });
          })
          .catch(() => {
            // error handling
            // console.log("error", error)
          })
          .finally(() => {
            isTriggeredRef.current = false;
          });
      }
    },
    [endCursor, fetchMore, hasNextPage, variables],
  );

  /** InterSectionObserver 추가 */
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, loaderEl, selectedTag]);

  return {
    data,
    loading,
    networkStatus,
    products,
    hasNextPage,
  };
};

export default useCollections;
